import type {
  ActivityFeedItemDto,
  BalanceResponseDto,
  CategoryReportDto,
  CreateExpenseDto,
  DebtRecommendationDto,
  ExpenseResponseDto,
  GroupMembersReportDto,
  GroupSpendingReportDto,
  MemberExpenseReportDto,
  MonthlyReportDto,
  PaginatedResponseDto,
  SettlementResponseDto,
  SimplifiedDebtsDto,
  SpendingTrendsDto,
  SuccessResponseDto,
  UpdateExpenseDto,
  YearlyReportDto,
} from '@splitfiesta/shared';

import { EXAMPLE_GROUP_ID, type ExampleGroup } from './exampleGroup';
import {
  EXAMPLE_ADDED_PREFIX,
  applyExampleUpdate,
  exampleAddsIn,
  toExampleExpenseDto,
  useExampleSandboxStore,
  validateExampleExpense,
  withSandbox,
} from './exampleSandbox';

/**
 * Serves the example group from the API client itself, so every existing
 * screen — list, detail, all six tabs, expense detail, reports — renders
 * it with zero special-casing. Wrap each app's BaseApi once:
 *
 *   const api = withExampleGroup(rawApi, provider);
 *
 * Reads whose path names the example (`/groups/example/**`,
 * `/expenses/group/example`, `/expenses/example-*`, `/settlements/group/
 * example`, `/balance/group/example`) get fixture payloads; nothing is
 * fetched. `DELETE /groups/example` hides it through the provider, so
 * the real delete flows (swipe → Delete, settings → Delete group) just
 * work. An expense create in the example, and the edit or delete of one
 * created that way, play out in the in-memory sandbox (exampleSandbox.ts),
 * whose adds every read merges in. Every other write that targets the
 * example is refused with a coded, human-readable error. Paths that don't name the example pass
 * straight through, untouched — including any extra arguments the
 * platform's client takes (web passes request options).
 */

export const EXAMPLE_GROUP_READ_ONLY = 'EXAMPLE_GROUP_READ_ONLY';

export interface ExampleGroupProvider {
  /**
   * The example, or null when it is hidden / no user is signed in. May be
   * async — web loads its i18n namespace over HTTP first, so the served
   * names are never bare keys.
   */
  build: () => ExampleGroup | null | Promise<ExampleGroup | null>;
  /** Persist "hidden" — called for DELETE /groups/example. */
  onDelete: () => void;
  /** Already-translated copy for refused writes. */
  readOnlyMessage: () => string;
}

/**
 * Shaped so both platforms' error readers find the message: mobile reads
 * `message` / `code` off the thrown error, web's handleApiError looks in
 * `response.data.error`.
 */
export class ExampleGroupReadOnlyError extends Error {
  readonly code = EXAMPLE_GROUP_READ_ONLY;
  readonly status = 403;
  readonly response: { data: { error: { code: string; message: string } } };

  constructor(message: string) {
    super(message);
    this.name = 'ExampleGroupReadOnlyError';
    this.response = {
      data: { error: { code: EXAMPLE_GROUP_READ_ONLY, message } },
    };
  }
}

export const EXAMPLE_GROUP_NOT_FOUND = 'EXAMPLE_GROUP_NOT_FOUND';

/**
 * A read that names the example but nothing the current scenario has —
 * typically a bookmarked/reloaded `/expenses/example-expense-5` after the
 * rotation moved to a scenario with four expenses. Shaped like the API's
 * 404 so the screens' existing not-found handling applies; it must never
 * fall through to the real backend.
 */
export class ExampleGroupNotFoundError extends Error {
  readonly code = EXAMPLE_GROUP_NOT_FOUND;
  readonly status = 404;
  readonly response: {
    status: number;
    data: { error: { code: string; message: string } };
  };

  constructor(path: string) {
    super(`Not part of the example group: ${path}`);
    this.name = 'ExampleGroupNotFoundError';
    this.response = {
      status: 404,
      data: { error: { code: EXAMPLE_GROUP_NOT_FOUND, message: this.message } },
    };
  }
}

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';
type AnyApi = Record<Method, (...args: never[]) => Promise<unknown>>;

const EXAMPLE_PREFIX = 'example-';

function ok<T>(data: T): SuccessResponseDto<T> {
  return { success: true, data };
}

function pathOf(endpoint: string): string {
  return endpoint.split('?')[0];
}

function isExampleGroupPath(path: string): boolean {
  return (
    path === `/groups/${EXAMPLE_GROUP_ID}` ||
    path.startsWith(`/groups/${EXAMPLE_GROUP_ID}/`) ||
    path === `/expenses/group/${EXAMPLE_GROUP_ID}` ||
    path === `/settlements/group/${EXAMPLE_GROUP_ID}` ||
    path.startsWith(`/settlements/group/${EXAMPLE_GROUP_ID}/`) ||
    path === `/balance/group/${EXAMPLE_GROUP_ID}`
  );
}

/** `/expenses/example-expense-1`, `/settlements/example-…`, `/comments/example-…` */
function isExampleEntityPath(path: string): boolean {
  return /^\/(expenses|settlements|comments)\/example-/.test(path);
}

/**
 * Pro has no example to serve: the request is refused even while the example
 * is hidden, since the real API would only reject it later or, for DELETE,
 * act on a group id that is not real.
 */
function isProExampleRequest(path: string, body: unknown): boolean {
  if (path === `/pro/groups/${EXAMPLE_GROUP_ID}`) return true;
  if (path !== '/pro/groups' || !body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return (
    b.groupId === EXAMPLE_GROUP_ID || b.replaceGroupId === EXAMPLE_GROUP_ID
  );
}

/** An expense create names its group; one for a real group is never ours. */
function isCreateInRealGroup(
  method: Method,
  path: string,
  body: unknown
): boolean {
  if (method !== 'post' || path !== '/expenses') return false;
  return (body as { groupId?: unknown } | null)?.groupId !== EXAMPLE_GROUP_ID;
}

function bodyTargetsExample(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return (
    b.groupId === EXAMPLE_GROUP_ID ||
    b.entityId === EXAMPLE_GROUP_ID ||
    (typeof b.expenseId === 'string' && b.expenseId.startsWith(EXAMPLE_PREFIX))
  );
}

// ---------------------------------------------------------------- payloads

function activityFeed(ex: ExampleGroup): {
  activities: ActivityFeedItemDto[];
  pagination: { hasMore: boolean; nextCursor?: string };
} {
  const expenseItems: ActivityFeedItemDto[] = ex.expenses.map(e => ({
    // The API uses the expense id as the feed item id; the cards navigate
    // and comment with `item.id`.
    id: e.id,
    type: 'expense',
    user: e.payers[0]?.userName ?? '',
    userId: e.payers[0]?.userId ?? '',
    description: e.title,
    amount: e.amount,
    currency: e.currency,
    category: e.category,
    groupName: ex.group.name,
    groupId: ex.group.id,
    timestamp: e.createdAt,
    expenseDate: e.date,
    paidBy: e.payers.map(p => ({
      userId: p.userId,
      amount: p.amount,
      userName: p.userName,
    })),
    splits: e.splits.map(s => ({
      userId: s.userId,
      amount: s.amount,
      userName: s.userName,
    })),
    reactions: [],
    comments: [],
  }));
  // Expenses only — that is all the real feed service ever emits
  // (activity-feed.service.ts builds `type: 'expense'` items; payments
  // live on the Settlements tab). Mirroring that keeps the example from
  // advertising a feed entry the app does not produce.
  const activities = [...expenseItems].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp)
  );
  return { activities, pagination: { hasMore: false } };
}

function settlements(ex: ExampleGroup): SettlementResponseDto[] {
  const s = ex.settlement;
  const from = s.paidBy?.[0];
  const to = s.splits?.[0];
  return [
    {
      id: s.id,
      groupId: ex.group.id,
      fromUser: { id: from?.userId ?? '', name: from?.userName ?? '' },
      toUser: { id: to?.userId ?? '', name: to?.userName ?? '' },
      amount: s.amount,
      currency: s.currency,
      status: 'completed',
      method: 'cash',
      notes: s.description,
      settledAt: s.timestamp,
      createdAt: s.timestamp,
      canCancel: false,
      canConfirm: false,
      isRecipient: to?.userId === ex.group.createdBy,
    },
  ];
}

function simplifiedDebts(ex: ExampleGroup): SimplifiedDebtsDto {
  const name = (id: string) => ex.members.find(m => m.id === id)?.name ?? '';
  const recommendations: DebtRecommendationDto[] = ex.suggestions.map(
    (s, i) => ({
      fromUser: { id: s.fromUserId, name: name(s.fromUserId) },
      toUser: { id: s.toUserId, name: name(s.toUserId) },
      amount: s.amount,
      currency: ex.group.currency,
      priority: i + 1,
    })
  );
  return {
    groupId: ex.group.id,
    groupName: ex.group.name,
    currency: ex.group.currency,
    recommendations,
    pendingSettlements: [],
    totalTransactions: recommendations.length,
    potentialSavings: 0,
    lastCalculated: ex.group.updatedAt,
  };
}

function groupBalances(ex: ExampleGroup): {
  balances: Record<string, Record<string, number>>;
} {
  return {
    balances: Object.fromEntries(
      ex.group.balances.map(b => [b.userId, { [b.currency]: b.balance }])
    ),
  };
}

function balanceRows(ex: ExampleGroup): BalanceResponseDto[] {
  return ex.group.balances.map(b => ({
    userId: b.userId,
    name: b.userName,
    netBalance: b.balance,
    currency: b.currency,
    lastUpdated: ex.group.updatedAt,
  }));
}

function byCategory(ex: ExampleGroup): CategoryReportDto[] {
  const total = ex.expenses.reduce((a, e) => a + e.amount, 0);
  const groups = new Map<string, ExpenseResponseDto[]>();
  for (const e of ex.expenses) {
    const key = e.category ?? 'other';
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups.entries()]
    .map(([category, list]) => {
      const totalAmount = list.reduce((a, e) => a + e.amount, 0);
      return {
        category,
        totalAmount,
        expenseCount: list.length,
        averagePerExpense: totalAmount / list.length,
        percentage: Math.round((totalAmount / total) * 1000) / 10,
        subcategories: subcategoriesOf(list, category, totalAmount, total),
      };
    })
    .sort((a, b) => b.totalAmount - a.totalAmount);
}

/**
 * Same bucketing the API uses (group-report.service.ts
 * enrichWithSubcategories): an expense with no subcategory is filed under
 * the category's own name, so the drill-down's "no subcategory" handling
 * gets exercised by the example too.
 */
function subcategoriesOf(
  list: ExpenseResponseDto[],
  category: string,
  categoryTotal: number,
  total: number
): NonNullable<CategoryReportDto['subcategories']> {
  const buckets = new Map<string, ExpenseResponseDto[]>();
  for (const e of list) {
    const key = e.subcategory ?? category;
    buckets.set(key, [...(buckets.get(key) ?? []), e]);
  }
  return [...buckets.entries()]
    .map(([subcategory, rows]) => {
      const totalAmount = rows.reduce((a, e) => a + e.amount, 0);
      return {
        subcategory,
        totalAmount,
        expenseCount: rows.length,
        averagePerExpense: totalAmount / rows.length,
        percentageOfCategory:
          categoryTotal > 0
            ? Math.round((totalAmount / categoryTotal) * 10000) / 100
            : 0,
        percentageOfTotal:
          total > 0 ? Math.round((totalAmount / total) * 10000) / 100 : 0,
      };
    })
    .sort((a, b) => b.totalAmount - a.totalAmount);
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function spendingReport(
  ex: ExampleGroup,
  period: string
): GroupSpendingReportDto {
  const total = ex.expenses.reduce((a, e) => a + e.amount, 0);
  const dates = ex.expenses.map(e => e.date).sort();
  // Same shape the API returns (group-report.service.ts): topSpender and
  // settlementSummary are placeholders there, so they are here too —
  // the example never shows richer data than a real group can.
  const categoryInsights = byCategory(ex).map(c => ({
    category: c.category,
    total: c.totalAmount,
    percentage: c.percentage,
    count: c.expenseCount,
    topSpender: { userId: '', userName: '', amount: 0 },
    trend: 'stable' as const,
  }));
  return {
    groupId: ex.group.id,
    groupName: ex.group.name,
    period,
    startDate: dates[0] ?? ex.group.createdAt.slice(0, 10),
    endDate: dates[dates.length - 1] ?? ex.group.updatedAt.slice(0, 10),
    totalExpenses: total,
    averageExpenseAmount: total / ex.expenses.length,
    expenseCount: ex.expenses.length,
    currency: ex.group.currency,
    categoryInsights,
    settlementSummary: {
      totalUnsettled: 0,
      oldestUnsettledDays: 0,
      membersOwingMoney: 0,
    },
  };
}

function monthlyReport(ex: ExampleGroup): MonthlyReportDto[] {
  const months = new Map<string, ExpenseResponseDto[]>();
  for (const e of ex.expenses) {
    const k = monthKey(e.date);
    months.set(k, [...(months.get(k) ?? []), e]);
  }
  return [...months.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, list]) => {
      const totalAmount = list.reduce((a, e) => a + e.amount, 0);
      const categories: Record<string, number> = {};
      for (const e of list) {
        const k = e.category ?? 'other';
        categories[k] = (categories[k] ?? 0) + e.amount;
      }
      return {
        month,
        totalAmount,
        expenseCount: list.length,
        currency: ex.group.currency,
        categories,
        averagePerExpense: totalAmount / list.length,
      };
    });
}

function trendsReport(ex: ExampleGroup): SpendingTrendsDto[] {
  return monthlyReport(ex).map(m => ({
    month: m.month,
    totalAmount: m.totalAmount,
    expenseCount: m.expenseCount,
    changeFromPrevious: {
      amountChange: 0,
      amountPercentChange: 0,
      countChange: 0,
    },
  }));
}

function yearlyReport(ex: ExampleGroup, year: string): YearlyReportDto {
  const monthly = monthlyReport(ex).filter(m => m.month.startsWith(year));
  const total = monthly.reduce((a, m) => a + m.totalAmount, 0);
  return {
    year,
    totalAmount: total,
    expenseCount: monthly.reduce((a, m) => a + m.expenseCount, 0),
    currency: ex.group.currency,
    monthlyBreakdown: monthly.map(m => ({
      month: m.month,
      totalAmount: m.totalAmount,
      expenseCount: m.expenseCount,
      averagePerExpense: m.averagePerExpense,
    })),
    topCategories: byCategory(ex).map(c => ({
      category: c.category,
      totalAmount: c.totalAmount,
      percentage: c.percentage,
    })),
  };
}

function memberStats(ex: ExampleGroup, memberId: string) {
  let totalPaid = 0;
  let totalBenefited = 0;
  let expenseCount = 0;
  let expensesPaidCount = 0;
  for (const e of ex.expenses) {
    const paid = e.payers
      .filter(p => p.userId === memberId)
      .reduce((a, p) => a + p.amount, 0);
    const share = e.splits
      .filter(s => s.userId === memberId)
      .reduce((a, s) => a + s.amount, 0);
    if (paid > 0) expensesPaidCount += 1;
    if (paid > 0 || share > 0) expenseCount += 1;
    totalPaid += paid;
    totalBenefited += share;
  }
  return { totalPaid, totalBenefited, expenseCount, expensesPaidCount };
}

function membersReport(
  ex: ExampleGroup,
  period: string
): GroupMembersReportDto {
  const total = ex.expenses.reduce((a, e) => a + e.amount, 0);
  const members = ex.members.map(m => {
    const s = memberStats(ex, m.id);
    return {
      userId: m.id,
      userName: m.name,
      totalPaid: s.totalPaid,
      totalBenefited: s.totalBenefited,
      netContribution: s.totalPaid - s.totalBenefited,
      percentageOfGroupSpending: Math.round((s.totalPaid / total) * 1000) / 10,
      expenseCount: s.expenseCount,
    };
  });
  const by = <K extends keyof (typeof members)[number]>(k: K) =>
    [...members].sort((a, b) => Number(b[k]) - Number(a[k]));
  return {
    groupId: ex.group.id,
    groupName: ex.group.name,
    currency: ex.group.currency,
    period,
    startDate: ex.group.createdAt.slice(0, 10),
    endDate: ex.group.updatedAt.slice(0, 10),
    totals: {
      totalSpent: total,
      expenseCount: ex.expenses.length,
      memberCount: members.length,
    },
    members,
    rankings: {
      topContributors: by('totalPaid').map(m => ({
        userId: m.userId,
        userName: m.userName,
        amount: m.totalPaid,
      })),
      topBeneficiaries: by('totalBenefited').map(m => ({
        userId: m.userId,
        userName: m.userName,
        amount: m.totalBenefited,
      })),
      mostActive: by('expenseCount').map(m => ({
        userId: m.userId,
        userName: m.userName,
        count: m.expenseCount,
      })),
    },
  };
}

function memberReport(
  ex: ExampleGroup,
  memberId: string,
  period: string
): MemberExpenseReportDto {
  const m = ex.members.find(x => x.id === memberId) ?? ex.members[0];
  const s = memberStats(ex, m.id);
  const cats = new Map<string, { paid: number; count: number }>();
  for (const e of ex.expenses) {
    const paid = e.payers
      .filter(p => p.userId === m.id)
      .reduce((a, p) => a + p.amount, 0);
    if (paid === 0) continue;
    const k = e.category ?? 'other';
    const cur = cats.get(k) ?? { paid: 0, count: 0 };
    cats.set(k, { paid: cur.paid + paid, count: cur.count + 1 });
  }
  return {
    memberId: m.id,
    memberName: m.name,
    groupId: ex.group.id,
    currency: ex.group.currency,
    period,
    startDate: ex.group.createdAt.slice(0, 10),
    endDate: ex.group.updatedAt.slice(0, 10),
    summary: {
      totalPaid: s.totalPaid,
      totalBenefited: s.totalBenefited,
      netContribution: s.totalPaid - s.totalBenefited,
      generosityScore:
        s.totalBenefited > 0
          ? Math.max(
              0,
              Math.min(100, Math.round((s.totalPaid / s.totalBenefited) * 50))
            )
          : 50,
      expenseCount: s.expenseCount,
      expensesPaidCount: s.expensesPaidCount,
    },
    categoryBreakdown: [...cats.entries()].map(([category, c]) => ({
      category,
      totalPaid: c.paid,
      percentage:
        s.totalPaid > 0 ? Math.round((c.paid / s.totalPaid) * 1000) / 10 : 0,
      count: c.count,
    })),
    monthlyTrend: monthlyReport(ex).map(mo => {
      const inMonth = ex.expenses.filter(e => monthKey(e.date) === mo.month);
      const paid = inMonth.reduce(
        (a, e) =>
          a +
          e.payers
            .filter(p => p.userId === m.id)
            .reduce((x, p) => x + p.amount, 0),
        0
      );
      const ben = inMonth.reduce(
        (a, e) =>
          a +
          e.splits
            .filter(sp => sp.userId === m.id)
            .reduce((x, sp) => x + sp.amount, 0),
        0
      );
      return {
        month: mo.month,
        totalPaid: paid,
        totalBenefited: ben,
        netContribution: paid - ben,
      };
    }),
    recentExpenses: ex.expenses.map(e => {
      const paid = e.payers
        .filter(p => p.userId === m.id)
        .reduce((a, p) => a + p.amount, 0);
      const ben = e.splits
        .filter(sp => sp.userId === m.id)
        .reduce((a, sp) => a + sp.amount, 0);
      return {
        id: e.id,
        title: e.title,
        amount: e.amount,
        date: e.date,
        category: e.category,
        role:
          paid > 0 && ben > 0
            ? ('both' as const)
            : paid > 0
              ? ('payer' as const)
              : ('participant' as const),
        paidAmount: paid,
        benefitedAmount: ben,
      };
    }),
  };
}

function pagedExpenses(
  ex: ExampleGroup,
  endpoint: string
): PaginatedResponseDto<ExpenseResponseDto> {
  const q = new URLSearchParams(endpoint.split('?')[1] ?? '');
  const page = Number(q.get('page') ?? '1');
  const limit = Number(q.get('limit') ?? '20');
  // The real endpoint narrows on these, and the reports drill-down sends
  // them; without the same filtering the example's list would contradict
  // the chart above it.
  const category = q.get('category');
  const subcategory = q.get('subcategory');
  const startDate = q.get('startDate');
  const endDate = q.get('endDate');
  const matches = ex.expenses.filter(e => {
    if (category && e.category !== category) return false;
    if (subcategory && e.subcategory !== subcategory) return false;
    const day = e.date.slice(0, 10);
    if (startDate && day < startDate) return false;
    if (endDate && day > endDate) return false;
    return true;
  });
  // Newest first like the real list; the fixture is authored oldest first,
  // and an add made this launch is always the newest.
  const items =
    page <= 1
      ? [...matches].sort((x, y) => y.createdAt.localeCompare(x.createdAt))
      : [];
  return { items, total: matches.length, page, limit, hasMore: false };
}

// ---------------------------------------------------------------- routing

function serveRead(ex: ExampleGroup, endpoint: string): unknown | undefined {
  const path = pathOf(endpoint);
  const q = new URLSearchParams(endpoint.split('?')[1] ?? '');
  const g = `/groups/${EXAMPLE_GROUP_ID}`;

  if (path === g) return ok(ex.group);
  if (path === `${g}/simplified-debts`) return ok(simplifiedDebts(ex));
  if (path === `${g}/activity`) return ok(activityFeed(ex));
  if (path === `${g}/balances`) return ok(balanceRows(ex));
  if (path === `${g}/reports/spending`)
    return ok(spendingReport(ex, q.get('period') ?? 'all'));
  if (path === `${g}/reports/category`) return ok(byCategory(ex));
  if (path === `${g}/reports/monthly`) return ok(monthlyReport(ex));
  if (path === `${g}/reports/trends`) return ok(trendsReport(ex));
  if (path === `${g}/reports/yearly`)
    return ok(
      yearlyReport(ex, q.get('year') ?? new Date().getFullYear().toString())
    );
  if (path === `${g}/reports/members`)
    return ok(membersReport(ex, q.get('period') ?? 'all'));
  const member = path.match(new RegExp(`^${g}/reports/members/([^/]+)$`));
  if (member) return ok(memberReport(ex, member[1], q.get('period') ?? 'all'));
  if (path === `/expenses/group/${EXAMPLE_GROUP_ID}`)
    return ok(pagedExpenses(ex, endpoint));
  const expense = path.match(/^\/expenses\/(example-(?:expense|added)-\d+)$/);
  if (expense) {
    const found = ex.expenses.find(e => e.id === expense[1]);
    return found ? ok(found) : undefined;
  }
  if (path === `/settlements/group/${EXAMPLE_GROUP_ID}`)
    return ok({ settlements: settlements(ex) });
  if (path === `/settlements/group/${EXAMPLE_GROUP_ID}/summary`) return ok({});
  if (path === `/balance/group/${EXAMPLE_GROUP_ID}`)
    return ok(groupBalances(ex));
  return undefined;
}

function serveSandboxWrite(
  fixture: ExampleGroup,
  method: Method,
  path: string,
  body: unknown
): Promise<unknown> | undefined {
  const sandbox = useExampleSandboxStore.getState();
  const memberIds = fixture.members.map(m => m.id);
  try {
    if (method === 'post' && path === '/expenses') {
      const dto = body as CreateExpenseDto;
      if (dto?.groupId !== EXAMPLE_GROUP_ID) return undefined;
      validateExampleExpense(dto, memberIds);
      const added = sandbox.add(dto, fixture.group.createdBy, fixture.scenario);
      return Promise.resolve(ok(toExampleExpenseDto(added, fixture)));
    }
    const target = path.match(
      new RegExp(`^/expenses/(${EXAMPLE_ADDED_PREFIX}\\d+)$`)
    )?.[1];
    if (!target || (method !== 'put' && method !== 'delete')) return undefined;
    // Another scenario's add is not in this group.
    const existing = exampleAddsIn(sandbox.added, fixture.scenario).find(
      e => e.id === target
    );
    if (!existing) return Promise.reject(new ExampleGroupNotFoundError(path));
    if (method === 'delete') {
      sandbox.remove(target);
      return Promise.resolve(ok(undefined));
    }
    const dto = (body ?? {}) as UpdateExpenseDto;
    validateExampleExpense(applyExampleUpdate(existing, dto), memberIds);
    const updated = sandbox.update(target, dto);
    return Promise.resolve(
      ok(updated && toExampleExpenseDto(updated, fixture))
    );
  } catch (error) {
    return Promise.reject(error);
  }
}

export function withExampleGroup<A extends AnyApi>(
  api: A,
  provider: ExampleGroupProvider
): A {
  const wrap = (method: Method) =>
    ((...args: unknown[]) => {
      const [endpoint, body] = args as [string, unknown];
      const original = api[method] as (...a: unknown[]) => Promise<unknown>;
      if (typeof endpoint !== 'string') return original(...args);
      // Only requests that name the example pay for building the fixture;
      // everything else goes straight through.
      const path = pathOf(endpoint);
      if (isProExampleRequest(path, body)) {
        return Promise.reject(
          new ExampleGroupReadOnlyError(provider.readOnlyMessage())
        );
      }
      const targetsExample =
        isExampleGroupPath(path) ||
        isExampleEntityPath(path) ||
        bodyTargetsExample(body);
      if (!targetsExample || isCreateInRealGroup(method, path, body))
        return original(...args);
      return Promise.resolve(provider.build()).then(ex =>
        ex ? serve(ex, method, endpoint, body) : original(...args)
      );
    }) as A[Method];

  const serve = (
    fixture: ExampleGroup,
    method: Method,
    endpoint: string,
    body: unknown
  ): Promise<unknown> => {
    const path = pathOf(endpoint);
    const sandbox = useExampleSandboxStore.getState();
    const ex = withSandbox(fixture, sandbox.added);

    if (method === 'get') {
      // Anything that names the example is answered here — an id the
      // current scenario lacks is a 404, never a trip to the real API.
      const served = serveRead(ex, endpoint);
      return served !== undefined
        ? Promise.resolve(served)
        : Promise.reject(new ExampleGroupNotFoundError(path));
    }
    if (method === 'delete' && path === `/groups/${EXAMPLE_GROUP_ID}`) {
      provider.onDelete();
      return Promise.resolve(ok(undefined));
    }
    const write = serveSandboxWrite(fixture, method, path, body);
    if (write) return write;
    // The favorite heart is harmless — let it no-op rather than nag.
    if (method === 'put' && path === `/groups/${EXAMPLE_GROUP_ID}/favorite`) {
      return Promise.resolve(ok(undefined));
    }
    return Promise.reject(
      new ExampleGroupReadOnlyError(provider.readOnlyMessage())
    );
  };

  return {
    ...api,
    get: wrap('get'),
    post: wrap('post'),
    put: wrap('put'),
    patch: wrap('patch'),
    delete: wrap('delete'),
  };
}
